import { inject, Injectable } from "@angular/core";
import { ITranslateObject, JobsBillingCard } from "../views-interfaces";
import { MultiSelectModel, Option } from "@app/filter-menu/filter-types";
import { IFirmGroupsService } from "@app/shared";
import { LangChangeEvent, TranslateService } from "@ngx-translate/core";

@Injectable()
export class JobsGroupFilterGeneratorService {
    private readonly iFirmGroupsService = inject(IFirmGroupsService);
    private readonly translateService = inject(TranslateService);
    private currentLanguage: keyof ITranslateObject = "en-CA";
    private noneText = "none";

    constructor() {
        this.currentLanguage = (this.translateService.getCurrentLang() as keyof ITranslateObject)
            ? (this.translateService.getCurrentLang() as keyof ITranslateObject)
            : ((this.translateService.getFallbackLang() ?? "en-CA") as keyof ITranslateObject);
        this.noneText = this.currentLanguage === "fr-CA" ? "aucun" : "none";

        this.translateService.onLangChange.subscribe((event: LangChangeEvent) => {
            this.currentLanguage = event.lang as keyof ITranslateObject;
            this.noneText = this.currentLanguage === "fr-CA" ? "aucun" : "none";
        });
    }

    async generateGroup1Model(selection: Option[]) {
        if (this.iFirmGroupsService.getGroup1Enabled() && this.iFirmGroupsService.groups?.group1.group1JobsEnabled) {
            const jobGroups1 = this.iFirmGroupsService.getGroup1Options(false);

            const options: Option[] = (jobGroups1 ?? [])
                .map((option) => ({
                    text: {
                        "en-CA": option["en-CA"],
                        "fr-CA": option["fr-CA"],
                    } as ITranslateObject,
                    value: option.Id.toString(),
                }))
                .sort((a, b) => {
                    const textA = (typeof a.text === "string" ? a.text : a.text[this.currentLanguage] || "").toLowerCase();
                    const textB = (typeof b.text === "string" ? b.text : b.text[this.currentLanguage] || "").toLowerCase();
                    if (textA === this.noneText) return -1;
                    if (textB === this.noneText) return 1;
                    return textA.localeCompare(textB);
                });

            return {
                label: {
                    "en-CA": this.iFirmGroupsService.getGroup1Caption("en-CA"),
                    "fr-CA": this.iFirmGroupsService.getGroup1Caption("fr-CA"),
                } as ITranslateObject,
                applicableOptions: options,
                allOptions: options,
                selection: selection.filter((selected) => options.some((applicable) => selected.value === applicable.value)),
            } as MultiSelectModel;
        }

        return;
    }

    async generateGroup2Model(selection: Option[]) {
        if (this.iFirmGroupsService.getGroup2Enabled() && this.iFirmGroupsService.groups?.group2.group2JobsEnabled) {
            const jobGroups2 = this.iFirmGroupsService.getGroup2Options(false);

            const options: Option[] = (jobGroups2 ?? [])
                .map((option) => ({
                    text: {
                        "en-CA": option["en-CA"],
                        "fr-CA": option["fr-CA"],
                    } as ITranslateObject,
                    value: option.Id.toString(),
                }))
                .sort((a, b) => {
                    const textA = (typeof a.text === "string" ? a.text : a.text[this.currentLanguage] || "").toLowerCase();
                    const textB = (typeof b.text === "string" ? b.text : b.text[this.currentLanguage] || "").toLowerCase();
                    if (textA === this.noneText) return -1;
                    if (textB === this.noneText) return 1;
                    return textA.localeCompare(textB);
                });

            return {
                label: {
                    "en-CA": this.iFirmGroupsService.getGroup2Caption("en-CA"),
                    "fr-CA": this.iFirmGroupsService.getGroup2Caption("fr-CA"),
                } as ITranslateObject,
                applicableOptions: options,
                allOptions: options,
                selection: selection.filter((selected) => options.some((applicable) => selected.value === applicable.value)),
            } as MultiSelectModel;
        }

        return;
    }

    getPrintDescription(
        label: string | ITranslateObject,
        selectedOptions: Option[],
    ): { label: string | ITranslateObject; values: (string | ITranslateObject)[] } | null {
        if (!selectedOptions || selectedOptions.length === 0) {
            return null;
        } else {
            return {
                label,
                values: selectedOptions.map((value) => {
                    return value.text;
                }),
            };
        }
    }

    async generateGroup1Filter(selectedValues: string[]) {
        if (!selectedValues || selectedValues.length === 0) {
            return null;
        } else {
            return (card: JobsBillingCard) => {
                const group1 = card.job.group1;
                if (group1 === null) return selectedValues.includes("-1");
                return selectedValues.includes(group1);
            };
        }
    }

    async generateGroup2Filter(selectedValues: string[]) {
        if (!selectedValues || selectedValues.length === 0) {
            return null;
        } else {
            return (card: JobsBillingCard) => {
                const group2 = card.job.group2;
                if (group2 === null) return selectedValues.includes("-1");
                return selectedValues.includes(group2);
            };
        }
    }
}
